import React from "react";
import { Alert, Table, Tag, Typography } from "antd";
import type { TableRowSelection } from "antd/es/table/interface";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { CsvTransactionInput } from "../../../../../../api/graphql/mutations/transaction/bulkCreateCsvTransactions";
import { formatAmountInCents } from "../../../../../../utils";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

const { Text } = Typography;

const formatPaymentDate = (raw: string): string => {
  const parsed = moment(raw, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format(DISPLAY_DATE_FORMAT) : raw;
};

export interface PreviewRow extends CsvTransactionInput {
  _rowIndex: number;
  _hasError: boolean;
  _errorMessage?: string;
}

interface Props {
  rows: PreviewRow[];
  selectedKeys: number[];
  onSelectionChange: (keys: number[]) => void;
}

const PreviewStep = ({ rows, selectedKeys, onSelectionChange }: Props) => {
  const { t } = useTranslation("CsvImport");

  const errorCount = rows.filter((r) => r._hasError).length;
  const validRows = rows.filter((r) => !r._hasError);

  const rowSelection: TableRowSelection<PreviewRow> = {
    selectedRowKeys: selectedKeys,
    // onSelect handles individual row clicks across all pages
    onSelect: (record, selected) => {
      if (selected) {
        onSelectionChange([...selectedKeys, record._rowIndex]);
      } else {
        onSelectionChange(selectedKeys.filter((k) => k !== record._rowIndex));
      }
    },
    // onSelectAll handles the header checkbox — scoped to the current page only
    onSelectAll: (selected, _selectedRows, changeRows) => {
      const changeKeys = changeRows
        .filter((r) => !r._hasError)
        .map((r) => r._rowIndex);
      if (selected) {
        onSelectionChange([...new Set([...selectedKeys, ...changeKeys])]);
      } else {
        const removeSet = new Set(changeKeys);
        onSelectionChange(selectedKeys.filter((k) => !removeSet.has(k)));
      }
    },
    getCheckboxProps: (record) => ({ disabled: record._hasError }),
  };

  const columns = [
    {
      title: t("previewColDate"),
      dataIndex: "paymentDate",
      key: "paymentDate",
      width: 110,
      render: (paymentDate: string) => formatPaymentDate(paymentDate),
    },
    {
      title: t("previewColAmount"),
      dataIndex: "amount",
      key: "amount",
      width: 120,
      render: (amount: number, record: PreviewRow) =>
        record._hasError ? (
          <Tag color="red">{record._errorMessage}</Tag>
        ) : (
          <Text style={{ color: amount < 0 ? "#cf1322" : "#3f8600" }}>
            {formatAmountInCents(amount)}
          </Text>
        ),
    },
    {
      title: t("previewColCurrency"),
      dataIndex: "currency",
      key: "currency",
      width: 80,
      render: (currency: string) =>
        currency && currency !== "EUR" ? (
          <Text strong style={{ color: "#1677ff" }}>
            {currency}
          </Text>
        ) : (
          <Text type="secondary">{currency || "EUR"}</Text>
        ),
    },
    {
      title: t("previewColPaidBy"),
      dataIndex: "name",
      key: "name",
      render: (name?: string) => name || <Text type="secondary">—</Text>,
    },
    {
      title: t("previewColIban"),
      dataIndex: "iban",
      key: "iban",
      render: (iban?: string) => iban || <Text type="secondary">—</Text>,
    },
    {
      title: t("previewColDescription"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc?: string) => desc || <Text type="secondary">—</Text>,
    },
  ];

  return (
    <div>
      {errorCount > 0 && (
        <Alert
          type="warning"
          message={t("previewErrorRows", { count: errorCount })}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        {t("previewSelection", {
          selected: selectedKeys.length,
          total: validRows.length,
        })}{" "}
        <Tag>Import</Tag>
      </Text>

      <Table<PreviewRow>
        dataSource={rows}
        columns={columns}
        rowKey="_rowIndex"
        rowSelection={rowSelection}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        size="small"
        rowClassName={(record) =>
          record._hasError ? "csv-import-row-error" : ""
        }
      />
    </div>
  );
};

export default PreviewStep;
